// Shared popover hook for the v2 shell.
//
// Wires: click-outside (mousedown), Escape key, focus restoration on close.
// Matches the focus-restoration pattern from CHG-1223's CommandPalette.
//
// Per .forge/plans/v2-nav-shell.md.

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export interface UsePopoverResult {
  open: boolean;
  anchorRef: RefObject<HTMLElement>;
  contentRef: RefObject<HTMLElement>;
  setOpen: (next: boolean) => void;
  toggle: () => void;
  close: () => void;
}

export function usePopover(): UsePopoverResult {
  const [open, setOpenState] = useState(false);
  // We type the refs as HTMLElement so any element (button + div + ul + …)
  // can attach without explicit narrowing.
  const anchorRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const lastFocusRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => setOpenState(false), []);
  const setOpen = useCallback((next: boolean) => setOpenState(next), []);
  const toggle = useCallback(() => setOpenState((prev) => !prev), []);

  // Save the previously-focused element when opening; restore when closing.
  useEffect(() => {
    if (open) {
      lastFocusRef.current = document.activeElement as HTMLElement | null;
    } else {
      const last = lastFocusRef.current;
      // isConnected guards against the saved element having been removed
      // from the DOM while the popover was open (parent unmount, route change,
      // etc.). Focusing a detached node is a silent no-op; checking first
      // lets us fall back to a sensible default later if needed.
      if (last && last.isConnected && typeof last.focus === "function") {
        last.focus();
      }
    }
  }, [open]);

  // Click-outside + Escape close. Listeners are only attached while open so
  // closed popovers don't pay for global event traffic.
  useEffect(() => {
    if (!open) return;

    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      const anchor = anchorRef.current;
      const content = contentRef.current;
      if (anchor && anchor.contains(target)) return;
      if (content && content.contains(target)) return;
      setOpenState(false);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpenState(false);
      }
    }

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return { open, anchorRef, contentRef, setOpen, toggle, close };
}
